import React, { useState } from 'react';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useManagerContext } from '../../../contexts/ManagerContext';
import DocumentSubcategoriesList from './DocumentSubcategoriesList';
import DocumentTypesList from './DocumentTypesList';
import DocumentFieldsList from './DocumentFieldList';
import styles from '../../../styles/DocumentManagement.module.css';
import { DocumentCategoryItemProps } from '../../../types/DocumentManagement';

// Props interface now imported from types file

const DocumentCategoryItem: React.FC<DocumentCategoryItemProps> = React.memo(({ 
  category, 
  stats, 
  getSubcategoriesForCategory: propGetSubcategoriesForCategory,
  isCompanyMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Conditionally access the appropriate context based on mode
  const documentContext = useDocumentContext();
  const managerContext = useManagerContext();
  
  React.useEffect(() => {
    if (documentContext.expandedCategoryId === category.id) {
      setIsExpanded(true);
    }
  }, [documentContext.expandedCategoryId, category.id]);
  
  // Use appropriate helper methods based on mode and props
  const getSubcategoriesForCategory = propGetSubcategoriesForCategory || 
    (isCompanyMode ? managerContext.getCompanySubcategoriesForCategory : documentContext.getSubcategoriesForCategory);
  
  // Get document types and fields directly under category (no subcategory)
  const getDocumentTypesForCategory = isCompanyMode 
    ? managerContext.getCompanyDocumentTypesForCategory 
    : documentContext.getDocumentTypesForCategory;
  const getFieldsForCategory = isCompanyMode 
    ? managerContext.getCompanyFieldsForCategory 
    : documentContext.getFieldsForCategory;
  
  // Get subcategories using the appropriate method
  const subcategories = getSubcategoriesForCategory(category.id);
  
  // Get direct category items (items without subcategory)
  const directDocumentTypes = getDocumentTypesForCategory ? getDocumentTypesForCategory(category.id) : [];
  const directFields = getFieldsForCategory ? getFieldsForCategory(category.id) : [];
  const hasDirectItems = directDocumentTypes.length > 0 || directFields.length > 0;

  const getCompletionColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  const getBarFillClass = (rate: number) => {
    if (rate >= 90) return styles.progressBarFillSuccess;
    if (rate >= 70) return styles.progressBarFillWarning;
    return styles.progressBarFillDanger;
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };


  return (
    <div className={`${styles['doc-category-card']} ${stats ? styles[`completion-${getCompletionColor(stats.completionRate)}`] : ''}`}>
      <div className={styles['doc-category-header']} onClick={toggleExpanded}>
        <div className={styles['category-info']}>
          <h4 className={styles['doc-category-title']}>
            {category.name}
          </h4>
        </div>
        <div className={styles['category-actions']}>
          {stats && (
            <div className={styles['completion-summary']}>
              <span className={`${styles['completion-rate']} ${styles[`completion-${getCompletionColor(stats.completionRate)}`]}`}>
                {stats.completionRate.toFixed(1)}%
              </span>
              <div className={styles.miniProgressBarTrack}>
                <div
                  className={`${styles.miniProgressBarFill} ${getBarFillClass(stats.completionRate)}`}
                  style={{ width: `${Math.min(stats.completionRate, 100)}%` }}
                />
              </div>
              <span className={styles['completion-details']}>
                ({stats.completedItems}/{stats.totalItems})
              </span>
            </div>
          )}
          {stats && (
            <div className={styles['stats-summary']}>
              {stats.missingItems > 0 && (
                <span className={`${styles['stat-badge']} ${styles['missing']}`}>
                  {stats.missingItems} missing
                </span>
              )}
              {stats.expiredItems > 0 && (
                <span className={`${styles['stat-badge']} ${styles['expired']}`}>
                  {stats.expiredItems} expired
                </span>
              )}
            </div>
          )}
          <span className={`${styles['expand-arrow']} ${isExpanded ? styles['expanded'] : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className={styles['doc-category-content']}>
          {hasDirectItems && (
            <div className={styles['direct-category-items']}>
              <DocumentTypesList 
                types={directDocumentTypes}
                isCompanyMode={isCompanyMode}
              />
              <DocumentFieldsList 
                fields={directFields}
                isCompanyMode={isCompanyMode}
              />
            </div>
          )}
          
          {/* Display subcategories */}
          {subcategories.length > 0 && (
            <DocumentSubcategoriesList 
              subcategories={subcategories} 
              categoryStats={stats}
              isCompanyMode={isCompanyMode}
            />
          )}
          
          {/* No items message */}
          {!hasDirectItems && subcategories.length === 0 && (
            <div className={styles['no-items-message']}>
              <p>No documents or fields configured for this category.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Add display name for debugging
DocumentCategoryItem.displayName = 'DocumentCategoryItem';

export default DocumentCategoryItem;
